import React, { useCallback } from 'react';
import { FixedMultiplierField as SharedFixedMultiplierField } from '../../shared/surcharge/FixedMultiplierField';
import { useShortTurnConfig } from '../ShortTurnConfigForm';

interface FixedMultiplierFieldProps {
  isFloor?: boolean;
}

export const FixedMultiplierField: React.FC<FixedMultiplierFieldProps> = ({ isFloor = false }) => {
  const { shortTurnConfig, onShortTurnConfigChange, rateMultipliers, customizedFields = {}, context = 'user' } = useShortTurnConfig();
  // Unified rate-offset fields (Phase 9). Both floor and non-floor use `floorTierCode`
  // on the unified model; the only non-floor path left is cosmetic for legacy call sites.
  const selectedCode = shortTurnConfig.floorTierCode ?? null;
  const customValue = isFloor ? (shortTurnConfig.floorTierCustomValue ?? null) : null;

  const onSelectionChange = useCallback((code: string | null) => {
    const update = isFloor
      ? { floorTierCode: code, ...(code ? { floorTierCustomValue: null } : {}) }
      : { floorTierCode: code };
    onShortTurnConfigChange(update);
  }, [isFloor, onShortTurnConfigChange]);

  const onCustomValueChange = useCallback((value: number | null) => {
    onShortTurnConfigChange({ floorTierCustomValue: value });
  }, [onShortTurnConfigChange]);

  const customizedFieldKey = isFloor ? 'short_turn_floor_tier_code' : 'short_turn_floor_tier_code';

  // Filter multipliers for fixed_offset mode to only include those with thresholds
  const filteredMultipliers = React.useMemo(() => {
    if (!isFloor) {
      return rateMultipliers.filter(m =>
        m.defaultPerShiftThresholdHours != null || m.defaultPerWeekThresholdHours != null
      );
    }
    return rateMultipliers;
  }, [rateMultipliers, isFloor]);

  return (
    <SharedFixedMultiplierField
      isFloor={isFloor}
      rateMultipliers={filteredMultipliers}
      selectedCode={selectedCode}
      onSelectionChange={onSelectionChange}
      customValue={customValue}
      onCustomValueChange={isFloor ? onCustomValueChange : undefined}
      isCustomized={customizedFields[customizedFieldKey]}
      context={context}
      customizedFieldKey={customizedFieldKey}
    />
  );
};
